import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { chain } from "lodash";
import React, { useMemo } from "react";
import ProposalField, { ProposalFieldCategory, ProposalFieldName } from "work/proposals/proposal-fields/proposal-field";

/**
 * Walks the fields still waiting on the reviewer: the ones carrying an issue, and the ones carrying an
 * unread counterparty change.
 *
 * Unread comments used to be a third reason to stop, back when a comment belonged to a field. A
 * proposal has one conversation now, not one per field, so an unread comment is not about any field in
 * particular and cannot say which one to stop at — it is surfaced by the Comments/Changes pane instead.
 */
type IssueTraverserProps = {
  issueFields: ProposalField[];
  /** Fields whose only pending item is an unread counterparty change (no issue). */
  unreadChangeFields?: ProposalField[];
  currentField: ProposalField | undefined;
  prevLabel?: string;
  nextLabel?: string;
  disabled?: boolean;
  onTraverseToNewField: (newField: ProposalField) => void;
};

export default function IssueTraverser(props: Readonly<IssueTraverserProps>) {
  const {
    issueFields,
    unreadChangeFields,
    currentField,
    prevLabel,
    nextLabel,
    disabled,
    onTraverseToNewField,
  } = props;

  const proposalFieldCategories = useMemo(() => Object.values(ProposalFieldCategory), []);
  const proposalFieldNames = useMemo(() => Object.values(ProposalFieldName), []);
  const fieldsPendingReview: ProposalField[] = useMemo(() => {
    const combined = [...issueFields, ...(unreadChangeFields ?? [])];

    // Remove duplicates and sort
    return chain(combined)
      .uniqBy((field) => `${field.category}-${field.name}-${field.id?.value ?? ''}`)
      .sortBy([
        (field) => proposalFieldCategories.indexOf(field.category),
        (field) => proposalFieldNames.indexOf(field.name)
      ])
      .value();
  }, [issueFields, unreadChangeFields, proposalFieldCategories, proposalFieldNames]);

  const currentIndex = currentField ? fieldsPendingReview.findIndex((field) => field.isEqualTo(currentField)) : -1;

  const getPreviousFieldPendingReview = (): ProposalField | undefined => {
    if (fieldsPendingReview.length < 1) return;

    if (currentField) {
      let previousIndex = currentIndex >= 0 ? currentIndex - 1 : fieldsPendingReview.length - 1;

      if (previousIndex < 0) {
        previousIndex = fieldsPendingReview.length - 1;
      }

      return fieldsPendingReview[previousIndex];
    }

    return fieldsPendingReview.at(-1);
  }

  const getNextFieldPendingReview = (): ProposalField | undefined => {
    if (fieldsPendingReview.length < 1) return;

    if (currentField) {
      let nextIndex = currentIndex >= 0 ? currentIndex + 1 : 0;

      if (nextIndex >= fieldsPendingReview.length) {
        nextIndex = 0;
      }

      return fieldsPendingReview[nextIndex];
    }

    return fieldsPendingReview[0];
  }

  const hasOneCurrentField =
    fieldsPendingReview.length === 1 && Boolean(currentField) && fieldsPendingReview[0].isEqualTo(currentField);

  return (
    <ButtonGroup
      disabled={disabled}
      size="small"
      color="primary"
      variant="outlined"
      orientation="horizontal"
      onMouseDown={(event) => event.preventDefault()}
    >
      <Button
        id="previous-issue"
        disabled={disabled || fieldsPendingReview.length < 1 || hasOneCurrentField}
        sx={{ whiteSpace: 'nowrap' }}
        onClick={() => {
          const previousField = getPreviousFieldPendingReview();
          if (previousField) onTraverseToNewField(previousField);
        }}
      >
        <KeyboardArrowUpIcon />
        {prevLabel ?? "Prev Change"}
      </Button>
      <Button
        id="next-issue"
        disabled={disabled || fieldsPendingReview.length < 1 || hasOneCurrentField}
        sx={{ whiteSpace: 'nowrap' }}
        onClick={() => {
          const nextField = getNextFieldPendingReview();
          if (nextField) onTraverseToNewField(nextField);
        }}
      >
        {nextLabel ?? "Next Change"}
        <KeyboardArrowDownIcon />
      </Button>
    </ButtonGroup>
  );
}
